import { useCallback, useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';

import { useAnalytics } from '@restworld/utils-common';
import { Heading, Button, ModalConfirmContent } from '@restworld/ui-ds-base';

import { useRedirectRoute, useApplicationModalToggle, stepsMapping } from '../form-inputs.hooks';
import { CoffeeCupIcon } from '../svg';

export const WizardExitConfirm = ({ entryTime }: { entryTime?: number }) => {
  const { closeModal } = useApplicationModalToggle();
  const { redirectUserCallback } = useRedirectRoute();
  const router = useRouter();
  const { trackEvent } = useAnalytics();
  const isSpontaneousEntry = useMemo(
    () => router?.query?.isSpontaneousEntry === 'true',
    [router?.query?.isSpontaneousEntry]
  );
  const { t } = useTranslation();
  const activeStep = 0;
  const recordEvent = useCallback(() => {
    trackEvent('questionnaire_progress', {
      step_number: activeStep,
      step_name: stepsMapping[activeStep as number],
      time_spent: entryTime ? (Date.now() - entryTime) / 1000 : undefined,
      next_action: 'fill_later',
    });
  }, [activeStep, entryTime, trackEvent]);
  return isSpontaneousEntry ? (
    <ModalConfirmContent
      title={t('onboarding-page:end_the_questionnaire')}
      subtitle={t('onboarding-page:questionnaire_can_make_your_job_search_easier_in_the_future')}
      primaryAction={{
        label: t('common:complete_profile'),
        onClick: closeModal,
      }}
      secondaryAction={{
        label: t('common:exit_questionnaire'),
        onClick: () => router.push(`/locale/${router?.query?.id}`),
      }}
    />
  ) : (
    <div className="flex flex-col gap-16 justify-center items-center w-full pt-10">
      <div className="flex flex-col gap-4 items-center">
        <CoffeeCupIcon />
        <Heading size={3}>{t('common:pause_and_continue_later')}</Heading>
        <Heading color="muted" weight="normal" align="center">
          {t('onboarding-page:continue_filling_in_the_information_in_your_profile')}
        </Heading>
      </div>
      <div className="flex flex-col gap-4 w-full">
        <Button
          onClick={() => {
            recordEvent();
            redirectUserCallback();
          }}
          fullwidth
        >
          {t('common:continue_later')}
        </Button>
        <Button onClick={closeModal} variant="secondary-neutral" fullwidth>
          {t('common:cancel')}
        </Button>
      </div>
    </div>
  );
};
